import { CUSTOMER_LEVEL } from '@wyz/types';
import {
  AbsoluteSpinner,
  Form as BaseForm,
  Card,
  Col,
  Label,
  Row,
  Title,
} from '@wyz/ui';
import { isNil } from 'lodash-es';
import * as React from 'react';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useManageCustomers } from '~/common/api';
import {
  DropzoneAreaField,
  NumberField,
  SelectField,
  TextField,
  TypeaheadField,
} from '~/common/components';
import { Trans, useLocale } from '~/common/context';
import { ROLES } from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';
import { CustomerGroupSelectField } from '~/connect/components';
import {
  useCustomerFormLevel,
  useCustomerFormParentCustomer,
  useCustomerFormPrivileges,
} from '~/connect/hooks';
import { CustomerFormProps, ICustomerGeneralForm } from '~/connect/types';
import { t } from 'i18next';

const NAME_FIELD_PARENT = 'parent.id';

const CustomersGeneralForm: React.FC<CustomerFormProps> = ({
  customer,
  mode,
}) => {
  const acl = useAccessControl();
  const { languages } = useLocale();
  const manageCustomers = useManageCustomers({
    isInitialized: { enabled: true },
    getOneById: {
      enabled: mode === 'UPDATE' && Boolean(customer?.id),
      args: { id: customer?.id as string },
    },
    autocomplete: { enabled: true },
  });

  const methods = useFormContext<ICustomerGeneralForm>();
  const parentCustomerQuery = useCustomerFormParentCustomer({
    methods: methods,
  });
  const watchedLevel = useCustomerFormLevel({
    parent: methods.getValues('parent'),
  });

  const originalLevel = manageCustomers.getOneById.data?.data.level;
  const parentCustomer = parentCustomerQuery.data?.data;
  const canEditAdvancedField = acl.isGranted(
    [
      ROLES.ROLE_CUSTOMER_CUSTOMER_GROUP_CREATE,
      ROLES.ROLE_CUSTOMER_CUSTOMER_GROUP_UPDATE,
    ],
    'affirmative',
  );
  const isCurrentlyUpdatingRoot =
    mode == 'UPDATE' && originalLevel === CUSTOMER_LEVEL.ROOT;

  const isParentRequired =
    !isCurrentlyUpdatingRoot &&
    (!canEditAdvancedField ||
      true === manageCustomers.isInitialized.data?.data);

  const FormPrivileges = useCustomerFormPrivileges({
    customer: customer,
    level: watchedLevel,
  });

  useEffect(() => {
    if (
      watchedLevel > CUSTOMER_LEVEL.N1 &&
      !canEditAdvancedField &&
      !isNil(parentCustomer?.customerGroup)
    ) {
      methods.setValue(
        'customerGroup.value',
        String(parentCustomer?.customerGroup?.id),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditAdvancedField, parentCustomer?.customerGroup, watchedLevel]);
  /* Parent is required when the user is Gestionnaire PDV OR we have already created the N-0 */
  useEffect(() => {
    methods.setValue('_requiredParent', isParentRequired);
  }, [isParentRequired, methods]);

  /* customerGroup is required when creating N1 */
  useEffect(() => {
    methods.setValue(
      '_requiredCustomerGroup',
      FormPrivileges.GENERAL_FORM.fields.GROUP_NAME.visible,
    );
  }, [
    FormPrivileges.GENERAL_FORM.fields.GROUP_NAME.visible,
    methods,
    watchedLevel,
  ]);

  return (
    <Card>
      <Card.Header>
        <Title heading='lg' color='tertiary' weight='bold'>
          <Trans i18nKey='app.entity.tab.general' />
        </Title>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col sm={3}>
            <TextField
              name='customerCode'
              label={<Trans i18nKey='app.entity.form.code' />}
              required
              readOnly={
                mode === 'UPDATE' &&
                !FormPrivileges.GENERAL_FORM.fields.CUSTOMER_CODE.writable
              }
            />
          </Col>
          <Col sm={3}>
            <TextField
              name='name'
              label={<Trans i18nKey='app.entity.form.name' />}
              required
              readOnly={
                !FormPrivileges.GENERAL_FORM.fields.DEFAULT_GENERAL_FIELDS
                  .writable
              }
            />
          </Col>
          <Col sm={3}>
            <TextField
              name='commercialName'
              label={<Trans i18nKey='app.entity.form.commercial_name' />}
              readOnly={
                !FormPrivileges.GENERAL_FORM.fields.DEFAULT_GENERAL_FIELDS
                  .writable
              }
            />
          </Col>
        </Row>
        <Row>
          <Col sm={4}>
            <TextField
              name='legalForm'
              label={<Trans i18nKey='app.entity.form.legal_form' />}
              placeholder={t('app.entity.form.legal_form.placeholder')}
              tooltips={<Trans i18nKey='app.entity.form.legal_form.tooltip' />}
              readOnly={
                !FormPrivileges.GENERAL_FORM.fields.DEFAULT_GENERAL_FIELDS
                  .writable
              }
            />
          </Col>
          <Col sm={4}>
            <TextField
              name='rcsNumber'
              label={<Trans i18nKey='app.entity.form.rcs_number' />}
              placeholder={t('app.entity.form.rcs_number.placeholder')}
              tooltips={<Trans i18nKey='app.entity.form.rcs_number.tooltip' />}
              readOnly={
                !FormPrivileges.GENERAL_FORM.fields.DEFAULT_GENERAL_FIELDS
                  .writable
              }
            />
          </Col>
          <Col sm={4}>
            <TextField
              name='vatNumber'
              label={<Trans i18nKey='app.entity.form.vat_number' />}
              placeholder={t('app.entity.form.vat_number.placeholder')}
              tooltips={<Trans i18nKey='app.entity.form.vat_number.tooltip' />}
              readOnly={
                !FormPrivileges.GENERAL_FORM.fields.DEFAULT_GENERAL_FIELDS
                  .writable
              }
            />
          </Col>
        </Row>
        <Row>
          {FormPrivileges.GENERAL_FORM.fields.GROUP_NAME.visible && (
            <Col sm={4}>
              <CustomerGroupSelectField
                readOnly={
                  !FormPrivileges.GENERAL_FORM.fields.DEFAULT_GENERAL_FIELDS
                    .writable
                }
              />
            </Col>
          )}
          <Col sm={6}>
            <TypeaheadField
              required={isParentRequired}
              isClearable
              minCharacter={3}
              minCharacterMessage={
                <Trans
                  i18nKey='app.common.autocomplete.min_character_message'
                  values={{
                    minCharacter: 3,
                  }}
                />
              }
              name={NAME_FIELD_PARENT}
              label={<Trans i18nKey='app.common.customer.parent' />}
              fetcher={(query, options) =>
                manageCustomers.autocomplete(query, {
                  ...options,
                  excluded: customer?.id ? [customer?.id] : [],
                  authorizedLevels: [CUSTOMER_LEVEL.ROOT, CUSTOMER_LEVEL.N1],
                })
              }
              isDisabled={
                !FormPrivileges.GENERAL_FORM.fields.DEFAULT_GENERAL_FIELDS
                  .writable
              }
            />
          </Col>
          <Col sm={2}>
            <Label>
              <Trans i18nKey='app.entity.form.level' />
            </Label>
            {/* we used BaseForm.Control instead of Textfield since this field is readonly AND computed */}
            <BaseForm.Control disabled value={watchedLevel} />
          </Col>
          {FormPrivileges.GENERAL_FORM.fields.LOGO.visible && (
            <Col sm={6}>
              <DropzoneAreaField
                name={'logo'}
                label={<Trans i18nKey='app.entity.form.logo' />}
                tooltips={<Trans i18nKey='app.entity.form.logo.tooltip' />}
                filesLimit={1}
                maxFileSize={1024 * 1024} // 1Mo
                resourceType='customer_logo_image'
                readOnly={!FormPrivileges.GENERAL_FORM.fields.LOGO.writable}
                scope='TENANT'
              />
            </Col>
          )}

          {FormPrivileges.GENERAL_FORM.fields.REDISTRIBUTION_MAX_DELAY
            .visible && (
            <Col sm={4}>
              <NumberField
                name='redistributionMaxDelay'
                label={
                  <Trans i18nKey='app.common.redistribution_max_delivery' />
                }
                min={1}
                max={100}
                disabled={
                  !FormPrivileges.GENERAL_FORM.fields.DEFAULT_GENERAL_FIELDS
                    .writable
                }
              />
            </Col>
          )}
        </Row>
        <Row>
          {FormPrivileges.GENERAL_FORM.fields.LABEL_FRONT_CHILDREN.visible && (
            <Col sm={4}>
              <TextField
                tooltips={<Trans i18nKey='app.entity.form.tooltip.children' />}
                name='customName'
                label={<Trans i18nKey='app.common.customer.custom_name' />}
                readOnly={
                  !FormPrivileges.GENERAL_FORM.fields.LABEL_FRONT_CHILDREN
                    .writable
                }
              />
            </Col>
          )}
          {FormPrivileges.GENERAL_FORM.fields.LOCALE.visible && (
            <Col sm={4}>
              <SelectField
                name='locale'
                label={<Trans i18nKey='app.common.language' />}
                options={
                  languages.byIndex?.map((language) => {
                    return {
                      label: language.humanName,
                      value: language.isoCode,
                    };
                  }) ?? []
                }
                required
                isDisabled={!FormPrivileges.GENERAL_FORM.fields.LOCALE.writable}
              />
            </Col>
          )}
        </Row>
        {parentCustomerQuery.isLoading && <AbsoluteSpinner />}
      </Card.Body>
    </Card>
  );
};

export default CustomersGeneralForm;
